/**
 * DateBuilder - Converts form inputs to engine format and generates date records
 * This builder handles the conversion from user-friendly form inputs to the engine's DateInputs format
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import { DATE_VALIDATION } from '~/config/municipal-bond-date-constants';
import type {
  DateInputs,
  DateInputsForm,
  DateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
  Project,
  ProjectDateElements,
  ProjectDateRecordByYear,
} from '~/types';
import { addYears, isValidDateString } from '~/utils/date-utils';
import {
  buildDateRecordsMapByPaymentDate,
  buildDateRecordsMapByTaxYear,
  buildDateSummary,
  constructPaymentDateKey,
  convertToEngineDate,
  determineIsLastPayment,
  findLastPaymentInFiscalYear,
} from './ProjectDatesHelper';

// Re-export types for backwards compatibility
export type { DateRecord, DateRecordsMapByPaymentDate, DateRecordsMapByTaxYear } from '~/types';

export class DateBuilder {
  static validate(formInputs: DateInputsForm): string[] {
    const errors: string[] = [];

    // Validate starting tax year
    if (!formInputs.startingTaxYear) {
      errors.push('Starting tax year is required');
    } else {
      const year = parseInt(formInputs.startingTaxYear, 10);
      if (
        Number.isNaN(year) ||
        year < DATE_VALIDATION.MIN_TAX_YEAR ||
        year > DATE_VALIDATION.MAX_TAX_YEAR
      ) {
        errors.push(
          `Starting tax year must be between ${DATE_VALIDATION.MIN_TAX_YEAR} and ${DATE_VALIDATION.MAX_TAX_YEAR}`
        );
      }
    }

    // Validate fiscal year end
    if (!formInputs.fiscalYearEnd) {
      errors.push('Fiscal year end date is required');
    } else if (!isValidDateString(formInputs.fiscalYearEnd)) {
      errors.push('Fiscal year end must be a valid date');
    }

    // Validate payment dates
    if (formInputs.paymentDates.length === 0) {
      errors.push('At least one payment date is required');
    }

    formInputs.paymentDates.forEach((payment, index) => {
      if (!payment.date) {
        errors.push(`Payment date ${index + 1} is required`);
      } else if (!isValidDateString(payment.date)) {
        errors.push(`Payment date ${index + 1} must be a valid date`);
      }

      if (!payment.revenuePercentage) {
        errors.push(`Revenue percentage for payment date ${index + 1} is required`);
      } else {
        const percentage = parseFloat(payment.revenuePercentage);
        if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
          errors.push(`Revenue percentage for payment date ${index + 1} must be between 0 and 100`);
        }
      }

      // Require explicit payment type selection (Principal+Interest or Interest Only)
      if (payment.isInterestOnly === undefined) {
        errors.push(`Payment date ${index + 1}: Select payment type (Principal+ or Interest Only)`);
      }
    });

    // Check if revenue percentages sum to 100%
    const totalPercentage = formInputs.paymentDates.reduce((sum, payment) => {
      return sum + (parseFloat(payment.revenuePercentage) || 0);
    }, 0);

    if (
      formInputs.paymentDates.length > 0 &&
      Math.abs(totalPercentage - 100) > DATE_VALIDATION.REVENUE_PERCENTAGE_TOLERANCE
    ) {
      errors.push('Revenue percentages must sum to 100%');
    }

    // Payment dates and fiscal year end are independent
    // Payment dates can occur after fiscal year end (e.g., in winter collection schedule)
    // No validation needed - they serve different purposes

    return errors;
  }

  static convertToEngineFormat(formInputs: DateInputsForm): DateInputs {
    const startingTaxYear = parseInt(formInputs.startingTaxYear, 10);

    // Convert payment dates to engine format
    const inputPaymentDateRecords = formInputs.paymentDates.map((payment) => {
      const isLastPayment = determineIsLastPayment(
        payment,
        formInputs.paymentDates,
        formInputs.fiscalYearEnd,
        formInputs.taxCollectionSchedule
      );

      return {
        paymentDate: convertToEngineDate(payment.date),
        revenueCollectionByDatePercentage: parseFloat(payment.revenuePercentage) / 100,
        isInterestOnly: payment.isInterestOnly ?? false, // Default to false if undefined
        isLastPaymentInFiscalYear: isLastPayment,
      };
    });

    // Determine last payment date of first fiscal year
    // For Winter tax schedule: November is the last payment (second payment)
    // For Summer tax schedule: May is the last payment (second payment)
    const lastPaymentDateOfFirstFiscalYear = findLastPaymentInFiscalYear(
      formInputs.paymentDates,
      formInputs.fiscalYearEnd,
      formInputs.taxCollectionSchedule
    );

    return {
      startingTaxYear,
      inputPaymentDateRecords,
      firstFiscalYearEnd: convertToEngineDate(formInputs.fiscalYearEnd),
      lastPaymentDateOfFirstFiscalYear: convertToEngineDate(lastPaymentDateOfFirstFiscalYear),
    };
  }

  static buildDateRecords(dateInputs: DateInputs): DateRecord[] {
    const records: DateRecord[] = [];
    for (let year = 0; year < TIME_PERIODS.PROJECT_YEARS; year++) {
      const taxYear = dateInputs.startingTaxYear + year;

      // Project payment dates for this year
      const paymentDates = dateInputs.inputPaymentDateRecords.map((record) => {
        return addYears(record.paymentDate, year);
      });

      // Project fiscal year end and last payment date
      const fiscalYearEndDate = addYears(dateInputs.firstFiscalYearEnd, year);
      const lastPaymentDateOfFiscalYear = addYears(
        dateInputs.lastPaymentDateOfFirstFiscalYear,
        year
      );

      records.push({
        projectYear: year + 1,
        taxYear,
        paymentDates,
        fiscalYearEndDate,
        lastPaymentDateOfFiscalYear,
      });
    }

    return records;
  }

  /**
   * Build the core ProjectDateRecordByYear array for enhanced processing
   * This serves as the foundation for all date-related calculations
   */
  static buildProjectDateRecords(project: Project): ProjectDateRecordByYear[] {
    if (!project.dateInputs || !project.dateElements?.dateRecords) {
      throw new Error(
        'Project must have date inputs and date records to build project date records'
      );
    }

    const projectDateRecords: ProjectDateRecordByYear[] = [];
    const { dateInputs, dateElements } = project;
    const dateRecords = dateElements.dateRecords;

    // Parse the starting tax year from the date inputs
    const initialTaxYear = parseInt(dateInputs.startingTaxYear, 10);
    if (Number.isNaN(initialTaxYear)) {
      throw new Error('Invalid starting tax year in date inputs');
    }

    // Extract initial payment dates from the first date record (which contains all payment dates for year 1)
    const initialPaymentDates = dateRecords?.[0]?.paymentDates || [];

    // Find fiscal year end and last payment date from the first year
    const firstFiscalYearEnd = dateInputs.fiscalYearEnd;
    const lastPaymentOfFirstFiscalYear =
      dateRecords?.[0]?.lastPaymentDateOfFiscalYear || firstFiscalYearEnd;

    // Generate project date records for standard project duration
    for (let i = 0; i < TIME_PERIODS.PROJECT_YEARS; i++) {
      const currentTaxYear = initialTaxYear + i;

      // Calculate payment dates for this year (add i years to each initial payment date)
      const currentPaymentDates = initialPaymentDates.map((date) => {
        return addYears(date, i);
      });

      // Calculate fiscal year end date for this year
      const currentFiscalYearEndDate = addYears(firstFiscalYearEnd, i);

      // Calculate last payment date for this year
      const currentLastPaymentDate = addYears(lastPaymentOfFirstFiscalYear, i);

      projectDateRecords.push({
        projectYear: i + 1,
        taxYear: currentTaxYear,
        paymentDates: currentPaymentDates,
        fiscalYearEndDate: currentFiscalYearEndDate,
        lastPaymentDateOfFiscalYear: currentLastPaymentDate,
      });
    }

    return projectDateRecords;
  }

  /**
   * Build the DateRecordsMapByTaxYear for efficient tax year lookups
   */
  static buildDateRecordsMapByTaxYear(
    projectDateRecords: ProjectDateRecordByYear[]
  ): DateRecordsMapByTaxYear {
    return buildDateRecordsMapByTaxYear(projectDateRecords);
  }

  /**
   * Build the DateRecordsMapByPaymentDate for efficient payment date lookups
   * This is the critical map that contains revenueCollectionByDatePercentage
   */
  static buildDateRecordsMapByPaymentDate(
    projectDateRecords: ProjectDateRecordByYear[],
    dateInputs: DateInputs
  ): DateRecordsMapByPaymentDate {
    return buildDateRecordsMapByPaymentDate(projectDateRecords, dateInputs);
  }

  /**
   * Main function to build base project elements from a demo project
   * Creates the essential date record maps needed for candidate processing
   */
  static buildBaseProjectElements(project: Project): ProjectDateElements {
    if (!project.dateElements?.dateRecords || project.dateElements.dateRecords.length === 0) {
      throw new Error('Project must have processed date records before creating date maps');
    }

    const dateInputs = DateBuilder.convertToEngineFormat(project.dateInputs!);
    const projectDateRecords = DateBuilder.buildProjectDateRecords(project);
    const dateRecordsMapByTaxYear = DateBuilder.buildDateRecordsMapByTaxYear(projectDateRecords);
    const dateRecordsMapByPaymentDate = DateBuilder.buildDateRecordsMapByPaymentDate(
      projectDateRecords,
      dateInputs
    );

    return {
      dateRecords: project.dateElements.dateRecords,
      dateRecordsMapByTaxYear,
      dateRecordsMapByPaymentDate,
    };
  }

  /**
   * Construct a payment date key for efficient lookups
   * Uses YYYYMMDD format as integer
   */
  static constructPaymentDateKey(paymentDate: string): number {
    return constructPaymentDateKey(paymentDate);
  }

  /**
   * Validates input parameters before building
   */
  static validateInputs(formInputs: DateInputsForm): { isValid: boolean; errors: string[] } {
    const errors = DateBuilder.validate(formInputs);

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Builds a summary of the date records
   */
  static buildSummary(data: DateRecord[]) {
    return buildDateSummary(data);
  }
}
